import { relativeTime } from '../../lib/format.ts';
import { isSeries } from './libraryFilterOptions.ts';
import type { LibraryItem } from '../../../shared/api.ts';

/**
 * The two lines of copy on an item card that are worth testing without a DOM:
 * the badge over the preview, and the meta line under the name.
 *
 * "N slides · saved 3 wks ago by Alex · used in 6 decks", with two clauses that
 * DROP rather than degrade. An item owns a deck now, so `savedAt` is always
 * there (it is the deck's own last edit); its owner is not: an item created
 * empty here, or saved before ownership was inherited from the source deck,
 * has no `ownerId`, and naming nobody beats inventing someone. An item no deck
 * has taken has an honest zero, and "used in 0 decks" is a sentence nobody
 * needs to read on every card of a new library. Both are the same rule the
 * decks card follows with its engine chip.
 *
 * `relative` is injectable purely so a test can assert the shape of the line
 * without pinning today. The default is the one the decks page's recent strip
 * and its cards use, so "3 mo ago" means the same thing on both pages.
 */

export type RelativeTime = (ms: number | null | undefined) => string;

/** "4 slides", "1 slide" — the count on its own, used by both lines below. */
export function slideCountLine(n: number): string {
  return `${n} ${n === 1 ? 'slide' : 'slides'}`;
}

/**
 * The badge over the preview, or null for a single slide.
 *
 * Only a SERIES gets one. A badge on every card would be a label rather than a
 * mark, and the thing worth marking is the card that brings four slides with it
 * when it is inserted — the single-slide card is the ordinary case and the meta
 * line under it already says "1 slide".
 */
export function libraryItemBadge(item: LibraryItem): string | null {
  return isSeries(item) ? `Series · ${slideCountLine(item.count)}` : null;
}

/** @param owner the name behind `item.ownerId`, when the people registry
 *  knows it; null or absent drops the "by" clause. */
export function libraryItemMetaLine(
  item: LibraryItem,
  relative: RelativeTime = relativeTime,
  owner: string | null = null,
): string {
  const parts = [slideCountLine(item.count)];
  parts.push(`saved ${relative(item.savedAt)}${owner ? ` by ${owner}` : ''}`);
  const decks = item.usedInDecks ?? 0;
  if (decks > 0) parts.push(`used in ${decks} deck${decks === 1 ? '' : 's'}`);
  return parts.join(' · ');
}
